import Form from "@/components/common/form/Form";
import DateInput from "@/components/common/form/inputs/date/DateInput";
import DateRangeInput from "@/components/common/form/inputs/date/DateRangeInput";
import DropdownInput from "@/components/common/form/inputs/dropdown/DropdownInput";
import FormActions from "@/components/common/form/partials/FormActions";
import If from "@/components/common/If";
import SeasonDropdown from "@/components/entities/app/season/inputs/SeasonDropdown";
import { SeasonType } from "@/types/season-type";
import { months } from "@/utilities/calendar-utilities";
import { DateFormats, DateUtil } from "@/utilities/date-utilities";
import useAppContext from "@/utilities/hooks/useAppContext";
import { useState } from "react";
import { useSearchParams } from "react-router-dom";

type FilterType = 'month' | 'until' | 'between' | '';

const today = new Date();

type FormModel = {
  season: SeasonType;
  month?: {
    id: string;
    name: string;
  };
  dateRange?: Date[];
  untilDate?: Date,
  filterType?: FilterType;
};

export type AcommodationStatsFilterFormSubmitModel = {
  seasonId: number;
  monthId?: string;
  dateRange?: string;
  untilDate?: string;
  filterType?: FilterType;
};

type AcommodationStatsFilterFormProps = {
  onSumit(model: AcommodationStatsFilterFormSubmitModel): void;
  onReset(): void;
  name: string,
  season: SeasonType,
};

const defaultSeason = {
  id: -1,
  name: "Active",
  active: true,
};

const AcommodationStatsFilterForm = (props: AcommodationStatsFilterFormProps) => {
  const context = useAppContext();
  const [searchParams] = useSearchParams();
  const [model, setModel] = useState<FormModel>({
    season: props.season,
    month: searchParams.get('monthFilter') ? {
      id: searchParams.get('monthFilter')!,
      name: context.t(`calendar.months.${searchParams.get('monthFilter')!}.name`)
    } : undefined,
    dateRange: [],
    untilDate: searchParams.get('untilDate') ? new Date(searchParams.get('untilDate')!) : undefined,
    filterType: (searchParams.get('filterType') as FilterType) || 'until'
  });

  const monthDataSource = months.map((m, index) => {
    return {
      id: m,
      name: context.t(`calendar.months.${m}.name`),
    };
  });

  function changeFilterType(filterType: FilterType) {
    setModel({ ...model, filterType });
  }

  function onFormSubmit(event: React.FormEvent<HTMLFormElement>) {
    props.onSumit({
      seasonId: model.season.id,
      monthId: model.filterType === 'month' ? model.month?.id : undefined,
      dateRange: model.filterType === 'between' && model.dateRange?.length
        ? `${DateUtil.format(model.dateRange[0], DateFormats.DATE)}-${DateUtil.format(model.dateRange[model.dateRange.length - 1], DateFormats.DATE)}`
        : undefined,
      untilDate: model.filterType === 'until' ? DateUtil.format(model.untilDate!, DateFormats.DATE) : undefined,
      filterType: model.filterType
    })
  }

  return (
    <Form name={`${props.name}-form`} onSubmit={onFormSubmit} onReset={props.onReset}>
      <SeasonDropdown
        name="field.sesason.name"
        onChange={(s) => setModel({ ...model, season: s || defaultSeason })}
        value={model.season}
        placeholder={context.t("fields.season.name")}
      />
      <div className="flex gap-4">
        <label>
          <input type="radio" name="filterType" value="until" checked={model.filterType === 'until'} onChange={() => changeFilterType('until')} /> <span>{context.t("accommodation.stats.until")}</span>
        </label>
        <label>
          <input type="radio" name="filterType" value="month" checked={model.filterType === 'month'} onChange={() => changeFilterType('month')} /> <span>{context.t("accommodation.stats.byMonth")}</span>
        </label>
        <label>
          <input type="radio" name="filterType" value="between" checked={model.filterType === 'between'} onChange={() => changeFilterType('between')} /> <span>{context.t("accommodation.stats.between")}</span>
        </label>
      </div>
      <If condition={model.filterType === 'month'}>
        <DropdownInput<{ id: string; name: string }, any, any>
          getItemId={(v) => v?.id}
          view={{
            inputView: (v) => v?.name,
          }}
          dataSource={{
            data: monthDataSource,
          }}
          value={model.month || undefined}
          onChange={(v) => setModel({ ...model, month: v })}
          name="accommodation-month-select-stats-page"
        />
      </If>
      <If condition={model.filterType === 'until'}>
        <DateInput
          name="untilDate"
          value={model.untilDate}
          min={new Date(today.getFullYear(), 0, 1)}
          max={new Date(today.getFullYear(), 11, 31)}
          placeholder="dd mm"
          onChange={(v) => setModel({ ...model, untilDate: v })}
        />
      </If>

      <If condition={model.filterType === 'between'}>
        <DateRangeInput
          name="dateRange"
          value={model.dateRange}
          min={new Date(today.getFullYear(), 1, 1)}
          max={new Date(today.getFullYear(), 11, 31)}
          onChange={(v) => setModel({ ...model, dateRange: v || [] })}
        />
      </If>

      <FormActions />
    </Form>
  );
};

export default AcommodationStatsFilterForm;
